import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import {
  AccessReportUser,
  listAccessReport,
} from '../../services/accessReportApi';

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('pt-BR');
}

function formatDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) {
    return '—';
  }

  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, '0')}min`;
  }
  if (minutes > 0) {
    return `${minutes}min ${secs.toString().padStart(2, '0')}s`;
  }
  return `${secs}s`;
}

export function AccessReportPage() {
  const [items, setItems] = useState<AccessReportUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      try {
        const result = await listAccessReport({ page, pageSize, search });
        if (cancelled) return;
        setItems(result.items);
        setTotalCount(result.totalCount);
      } catch {
        if (!cancelled) {
          toast.error('Falha ao carregar o relatório de acessos.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search]);

  function handleSearch(event: FormEvent): void {
    event.preventDefault();
    setPage(1);
    setSearch(searchDraft.trim());
  }

  return (
    <PageShell>
      <PageHeader
        title="Relatório de acessos"
        description="Usuários da empresa com último acesso e tempo médio de navegação (sessões dos últimos 90 dias)."
      />

      <form onSubmit={handleSearch} className="mb-4 flex flex-wrap items-end gap-2">
        <label className="erp-label min-w-[14rem] flex-1">
          Buscar
          <input
            className="erp-input"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Nome ou e-mail"
          />
        </label>
        <button type="submit" className="erp-btn-secondary">
          Filtrar
        </button>
      </form>

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <p className="text-sm text-secondary-500">Nenhum usuário encontrado.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-secondary-100 bg-secondary-50 text-xs uppercase text-secondary-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Usuário</th>
                  <th className="px-4 py-3 font-medium">E-mail</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Último acesso</th>
                  <th className="px-4 py-3 font-medium">Tempo médio</th>
                  <th className="px-4 py-3 font-medium">Sessões (90d)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {items.map((item) => (
                  <tr key={item.userId}>
                    <td className="px-4 py-3 font-medium text-secondary-900">{item.name}</td>
                    <td className="px-4 py-3 text-secondary-700">{item.email}</td>
                    <td className="px-4 py-3">
                      <ActiveStatusBadge active={item.isActive} />
                    </td>
                    <td className="px-4 py-3 text-secondary-700">
                      {formatDateTime(item.lastAccessAtUtc)}
                    </td>
                    <td className="px-4 py-3 text-secondary-700">
                      {formatDuration(item.averageNavigationSeconds)}
                    </td>
                    <td className="px-4 py-3 text-secondary-700">{item.sessionCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPaginationBar
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() =>
              setPage((current) =>
                current * pageSize < totalCount ? current + 1 : current,
              )
            }
          />
        </>
      )}
    </PageShell>
  );
}
